import React from 'react';
import { ChevronDown, Code2 } from 'lucide-react';
import { apiFetch } from '../shared/api/client';
import { Card, Button } from '../shared/ui';
import { theme, useTheme } from '../shared/config/theme';
import { ProviderCostCell, ProviderCostSummary } from './ProviderCost';
import './CodeActivityPanel.css';

const date = (value) => {
  if (value == null) return '—';
  const parsed = new Date(typeof value === 'number' ? value * 1000 : value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', dateStyle: 'short', timeStyle: 'short' });
};
const os = (value) => ({ macos: 'macOS', windows: 'Windows' }[value] || 'Неизвестна');
const cell = 'py-3 px-2 whitespace-nowrap';
const plan = (value) => ({ free: 'Free', mini: 'Mini', plus: 'Plus', pro: 'Pro', max: 'Max' }[value] || value || '—');

function AccountRow({ row, onSelectUser }) {
  const [expanded, setExpanded] = React.useState(false);
  const detailId = React.useId();
  return <>
    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
      <th scope="row" className="py-3 pr-4 text-left font-medium code-activity-account">
        {onSelectUser && row.user_id ? <button type="button" className="admin-user-link break-all" onClick={() => onSelectUser({ ...row, detail_product: 'noesa_code' })}>{row.email}</button> : <span className="block break-all">{row.email}</span>}
        <span className="block text-[11px] font-normal mt-1" style={{ color: theme.textSoft }}>{os(row.client_platform)}</span>
      </th>
      <td className={cell}>{plan(row.plan_code)}</td>
      <td className={cell} style={{ color: theme.textSoft }}>{date(row.last_activity_at)}</td>
      <td className={`${cell} text-right`}>{row.stats?.user_messages ?? '—'}</td>
      <td className={`${cell} text-right`}>{row.stats?.assistant_messages ?? '—'}</td>
      <td className="py-3 pl-2 text-right">
        <Button type="button" variant="outline" size="sm" aria-expanded={expanded} aria-controls={detailId}
          aria-label={`${expanded ? 'Скрыть' : 'Подробнее'}: ${row.email}, ${os(row.client_platform)}`}
          onClick={() => setExpanded(!expanded)}>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
        </Button>
      </td>
    </tr>
    <tr hidden={!expanded} id={detailId}>
      <td colSpan={6} className="p-4" style={{ background: theme.bgSoft, borderBottom: `1px solid ${theme.border}` }}>
        <dl className="grid gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          {[
            ['Первый вход', date(row.first_entry_at)],
            ['Первый запрос', date(row.first_request_at)],
            ['Первый ответ', date(row.first_response_at)],
            ['Источник', row.acquisition_source || 'Неизвестен'],
            ['Кампания', row.acquisition_campaign || 'Неизвестна'],
            ['Оплата в периоде', row.paid_in_period === true ? 'Подтверждена' : row.paid_in_period === false ? 'Нет подтверждённой оплаты' : '—'],
          ].map(([label, value]) => <div key={label} className="min-w-0">
            <dt className="text-[11px] mb-1" style={{ color: theme.textSoft }}>{label}</dt>
            <dd className="break-words">{value}</dd>
          </div>)}
        </dl>
        <p className="text-[11px] mt-3 leading-5" style={{ color: theme.textSoft }}>
          Первые действия — за всю доступную историю до конца периода. Оплата относится к общей подписке Noesa, не к отдельной подписке Code.
        </p>
      </td>
    </tr>
  </>;
}

export default function CodeActivityPanel({ data, loading, onSelectUser }) {
  useTheme();
  const [remote, setRemote] = React.useState(null);
  const [platform, setPlatform] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const request = React.useRef(null);
  React.useEffect(() => {
    request.current?.abort();
    setRemote(null); setPlatform(''); setOffset(0); setError(''); setBusy(false);
    return () => request.current?.abort();
  }, [data]);
  const code = remote || data?.noesa_code || {};
  const ready = !loading && code.activity_status === 'ready' && Boolean(code.activity);
  const activity = code.activity;
  const coverage = activity?.coverage || {};
  async function page(nextPlatform, nextOffset) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setError('');
    try {
      const query = new URLSearchParams({ date_from: data.date_from, date_to: data.date_to, offset: String(nextOffset) });
      if (nextPlatform) query.set('platform', nextPlatform);
      const response = await apiFetch('/analytics/admin/code/activity?' + query, { signal: controller.signal });
      const result = await response.json();
      if (request.current !== controller || controller.signal.aborted) return;
      setRemote(result); setPlatform(nextPlatform); setOffset(nextOffset);
    } catch (err) {
      if (!controller.signal.aborted) setError('Не удалось обновить активность. Попробуйте ещё раз.');
    } finally {
      if (request.current === controller) setBusy(false);
    }
  }
  const users = code.users || [];
  const models = code.models || [];
  return <Card padding="lg" className="code-activity-panel overflow-hidden" style={{ background: theme.bgCard, color: theme.text, borderColor: 'rgba(var(--brand-rgb),0.22)' }}>
    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
      <div className="flex items-start gap-4 min-w-0">
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: 'rgba(var(--brand-rgb),0.12)', color: theme.brand }}>
          <Code2 className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.14em] mb-1" style={{ color: theme.brand }}>Отдельный продукт</div>
          <h2 className="text-2xl font-semibold">Noesa Code</h2>
          <p className="text-sm leading-6 mt-2 max-w-3xl" style={{ color: theme.textSoft }}>
            Активность десктопного агента за выбранный период. Windows и macOS, пользователи и расходы на модели — отдельно от сайта и мобильного приложения.
          </p>
          {ready && <div className="text-[11px] mt-2" style={{ color: theme.textSoft }}>Данные на {date(activity.as_of)} · время Москва.</div>}
        </div>
      </div>
      <div className="flex flex-wrap lg:flex-col items-start gap-3 shrink-0">
        {ready && <span className="inline-flex rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: 'rgba(16,185,129,0.12)', color: theme.mode === 'dark' ? '#6ee7b7' : '#047857' }}>Хранилище Code · подключено</span>}
        <label className="text-xs font-medium w-full sm:w-auto" style={{ color: theme.textSoft }}>Платформа
          <select aria-label="Платформа Code" className="block rounded-xl border px-3 py-2 mt-1 min-h-11 w-full"
            style={{ background: theme.bgCard, color: theme.text, borderColor: theme.border }}
            value={platform} disabled={loading || busy} onChange={(e) => page(e.target.value, 0)}>
            <option value="">Все платформы</option><option value="windows">Windows</option><option value="macos">macOS</option>
          </select>
        </label>
      </div>
    </div>
    {error && <p role="alert" className="rounded-xl border p-4 mb-4" style={{ borderColor: theme.danger }}>{error} Показаны ранее загруженные данные.</p>}
    {loading && <p role="status" className="py-4" style={{ color: theme.textSoft }}>Загружаем активность Noesa Code…</p>}
    {!loading && !ready && <div role="status" className="rounded-xl border p-4 mb-5" style={{ borderColor: theme.border }}>
      {code.activity_status === 'not_configured' ? 'Подключение к хранилищу Code ещё не настроено.' : 'Актуальная активность Code временно недоступна.'}
      {' '}Это не означает отсутствие пользователей. История из старых таблиц не подставляется.
    </div>}
    {ready && <>
      {!coverage.history_scanned_at && <p role="status" className="text-xs mb-4 leading-5">Перенос доступной истории ещё выполняется — исторические показатели пока неполные.</p>}
      {coverage.invalid_history_dates > 0 && <p role="status" className="text-xs mb-4 leading-5">Записей без надёжной даты: {coverage.invalid_history_dates}. Даты не были придуманы.</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" aria-label="Показатели Code" aria-busy={busy}>
        {(code.metrics || []).map((metric) => <div key={metric.key} className="rounded-2xl p-4 min-w-0" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <div className="text-xs font-semibold leading-5" style={{ color: theme.textSoft }}>{metric.title}</div>
          <div className="text-2xl font-semibold tracking-tight mt-2">{metric.display_value}</div>
          <p className="text-[11px] mt-2 leading-4" style={{ color: theme.textSoft }}>{metric.description}</p>
        </div>)}
      </div>
    </>}
    {!loading && <>
      <ProviderCostSummary cost={code.provider_cost} code />
      <p className="text-xs leading-5 mt-3" style={{ color: theme.textSoft }}>Расходы и модели — все платформы Code. Фильтр ОС меняет только показатели активности; расходы между Windows и macOS не распределяются.</p>
      <div className="code-activity-columns grid gap-4 mt-5">
        <section className="rounded-2xl p-4 min-w-0" aria-label="Использование моделей" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <h3 className="text-sm font-semibold">Использование моделей</h3>
          <p className="text-xs leading-5 mt-1 mb-3" style={{ color: theme.textSoft }}>Учтённые операции и расходы на токены и инструменты.</p>
          <div className="flex flex-col gap-2">
            {models.map((row) => <div key={row.model} className="code-activity-model flex items-center justify-between gap-3 py-2" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <div className="min-w-0">
                <div className="text-xs font-mono break-words">{row.model}</div>
                <div className="text-[11px] mt-0.5" style={{ color: theme.textSoft }}>{row.rounds} учтённых операций</div>
              </div>
              <ProviderCostCell cost={row.provider_cost} />
            </div>)}
            {!models.length && <p className="text-xs py-3" style={{ color: theme.textSoft }}>{code.provider_cost ? 'В финансовом учёте за период нет операций по моделям.' : 'Данные о расходах на модели недоступны.'}</p>}
          </div>
        </section>
        <section className="rounded-2xl p-4 min-w-0" aria-label="Пользователи Noesa Code" style={{ background: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
            <div>
              <h3 className="text-sm font-semibold">Кто пользовался Noesa Code</h3>
              <p className="text-xs leading-5 mt-1" style={{ color: theme.textSoft }}>Входы и обращения за период. Источник и первые действия — в деталях строки.</p>
            </div>
            {ready && <div className="text-xs shrink-0" style={{ color: theme.textSoft }}>{users.length} строк на странице</div>}
          </div>
          {ready ? <>
            <div className="code-activity-scroll overflow-x-auto" role="region" aria-label="Таблица аккаунтов Code" tabIndex={0} aria-busy={busy}>
              <table className="min-w-full text-xs" aria-label="Аккаунты Code">
                <thead><tr style={{ color: theme.textSoft, borderBottom: `1px solid ${theme.border}` }}>
                  <th scope="col" className="text-left py-2.5 pr-4 font-medium">Email / ОС</th>
                  <th scope="col" className="text-left py-2.5 px-2 font-medium">Тариф</th>
                  <th scope="col" className="text-left py-2.5 px-2 font-medium">Последняя активность</th>
                  <th scope="col" className="text-right py-2.5 px-2 font-medium">Запросы</th>
                  <th scope="col" className="text-right py-2.5 px-2 font-medium">Ответы</th>
                  <th scope="col" className="text-right py-2.5 pl-2 font-medium">Детали</th>
                </tr></thead>
                <tbody>{users.map((row) => <AccountRow key={`${platform}:${offset}:${row.email}:${row.client_platform}`} row={row} onSelectUser={onSelectUser} />)}</tbody>
              </table>
            </div>
            {!users.length && <p className="text-xs py-6" style={{ color: theme.textSoft }}>Нет наблюдаемой активности аккаунтов за этот период.</p>}
            <div className="flex flex-wrap justify-between gap-3 items-center mt-4">
              <span className="text-xs" style={{ color: theme.textSoft }}>Страница {Math.floor(offset / 100) + 1} · аккаунт может быть на двух ОС</span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" disabled={busy || !offset} onClick={() => page(platform, Math.max(0, offset - 100))}>Назад</Button>
                <Button type="button" variant="outline" size="sm" disabled={busy || code.next_offset == null} onClick={() => page(platform, code.next_offset)}>Далее</Button>
              </div>
            </div>
          </> : <p className="text-xs py-6" style={{ color: theme.textSoft }}>Список появится после восстановления аналитики Code.</p>}
        </section>
      </div>
    </>}
    {ready && <>
      <section className="rounded-2xl p-4 min-w-0 mt-5" style={{ border: `1px solid ${theme.border}` }}>
        <h3 className="text-sm font-semibold mb-3">Windows и macOS</h3>
        <div className="code-activity-scroll overflow-x-auto" role="region" aria-label="Сравнение платформ Code" tabIndex={0} aria-busy={busy}>
          <table className="min-w-full text-xs" aria-label="Активность по платформам">
            <thead><tr style={{ color: theme.textSoft, borderBottom: `1px solid ${theme.border}` }}>{['Платформа', 'Клики', 'Полные передачи', 'Вошли (аккаунты)', 'Запросы', 'Успешные ответы', 'DAU', 'WAU', 'MAU'].map((name, i) => <th scope="col" className={`${cell} font-medium ${i ? 'text-right' : 'text-left'}`} key={name}>{name}</th>)}</tr></thead>
            <tbody>{(activity.platforms || []).map((row) => <tr key={row.platform} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <th scope="row" className={`${cell} text-left font-medium`}>{os(row.platform)}</th>
              {[row.stages?.download_click?.events, row.stages?.installer_transferred?.events, row.stages?.authenticated_entry?.accounts,
                row.stages?.request_accepted?.events, row.stages?.response_completed?.events, row.active?.day, row.active?.week, row.active?.month]
                .map((count, i) => <td key={i} className={`${cell} text-right`}>{count ?? '—'}</td>)}
            </tr>)}</tbody>
          </table>
        </div>
        <p className="text-[11px] leading-5 mt-3" style={{ color: theme.textSoft }}>Общий итог считает аккаунт один раз; сумма Windows и macOS может быть больше. Скачивание не означает установку, вход не означает использование ИИ.</p>
      </section>
      <details className="code-activity-methodology mt-5 text-xs leading-5" style={{ color: theme.textSoft }}>
        <summary className="cursor-pointer font-medium py-2">Как считаются показатели</summary>
        <div className="pt-2 space-y-2">
          <p>Вход — наблюдение авторизованного приложения; обновления его возможностей за день не создают повторные входы. Запросы и ответы — за выбранный период; первые действия — за всю доступную историю до его конца.</p>
          <p>Клики и передачи учитываются по новым отслеживаемым ссылкам, с учётом выбора аналитики; старые, прямые, кэшированные и частичные загрузки не восстанавливаются.</p>
          <p>Новые наблюдения с {date(coverage.tracking_started_at)}. «—» означает отсутствие данных. Только события и даты — без переписок, файлов и команд.</p>
        </div>
      </details>
    </>}
  </Card>;
}
